import { Skeleton } from "@credit-acceptance/uikit";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { EmptyState, ErrorState } from "../../components/ui/centeredMessage";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { StatusBadge } from "../../components/ui/statusBadge";
import { Switch } from "../../components/ui/switch";
import { getErrorMessage } from "../../lib/query/errors";
import { useDscWorkbench } from "../review/api/useDscWorkbench";
import type { StipulationView } from "./api/types";
import { useStipulations } from "./api/useStipulations";
import { buildBankAccountOwnerOptions, fullNameFromResidence } from "./bankAccountOwnerOptions";
import type { StipulationCategory } from "./stipulationCategoryFilters";
import {
  ALL_STIPULATION_CATEGORIES,
  APPLICANT_SCOPED_CATEGORIES,
  CATEGORY_TO_WORKBENCH_SCREEN,
  STIPULATION_CATEGORIES,
} from "./stipulationCategoryFilters";
import { useStipulationCategoryStore } from "./stipulationCategoryStore";
import { useStipulationEditsStore } from "./stipulationEditsStore";
import StipulationRow from "./StipulationRow";

const APPLICANT_ORDER_RANK: Record<string, number> = { driver: 0, secondSigner: 1 };

// A stable empty-array fallback — `data?.items ?? []` would allocate a new
// array every render while loading/erroring, defeating `visible`'s useMemo.
const EMPTY_ITEMS: StipulationView[] = [];

/** Stable-sorts Driver stips before Second Signer stips; stips with no `applicantType` keep their relative order at the end. */
function withDriverFirstOrder(items: StipulationView[]): StipulationView[] {
  return [...items].sort(
    (a, b) => (APPLICANT_ORDER_RANK[a.applicantType ?? ""] ?? 2) - (APPLICANT_ORDER_RANK[b.applicantType ?? ""] ?? 2)
  );
}

interface StipulationsPanelProperties {
  approvalId: string;
}

function StipulationsPanel({ approvalId }: StipulationsPanelProperties) {
  const { t } = useTranslation();
  const [openOnly, setOpenOnly] = useState(true);
  // Shared with the review page's scroll-spy (see ReviewScrollSpyProvider) —
  // scrolling through its sections drives this same store, always overriding
  // a manual pick as soon as the analyst scrolls again.
  const category = useStipulationCategoryStore((state) => state.category);
  const applicantScope = useStipulationCategoryStore((state) => state.applicantScope);
  const scrollScoped = useStipulationCategoryStore((state) => state.scrollScoped);
  const setCategory = useStipulationCategoryStore((state) => state.setCategory);
  const resetCategory = useStipulationCategoryStore((state) => state.reset);
  const resetEdits = useStipulationEditsStore((state) => state.reset);

  // Drop pending, unsaved stip edits and any scroll-driven category filter
  // when the open deal changes so one deal's in-progress state never leaks
  // into another.
  useEffect(() => {
    resetEdits();
    resetCategory();
  }, [approvalId, resetEdits, resetCategory]);

  const { data, isLoading, isError, error } = useStipulations({ approvalId, openOnly });
  const { data: dashboard } = useDscWorkbench(approvalId);

  const items = data?.items ?? EMPTY_ITEMS;
  const openCount = data?.openCount ?? 0;

  const visible = useMemo(() => {
    let result =
      category === ALL_STIPULATION_CATEGORIES
        ? items
        : items.filter((stip) => stip.workbenchScreens?.includes(CATEGORY_TO_WORKBENCH_SCREEN[category]));

    // Only Income/Bank/Residence live in the Signers card's applicant columns,
    // and only a scroll-spy report (`scrollScoped`) carries applicant context —
    // other sections and manual dropdown picks show every applicant in the
    // server's own order.
    if (category !== ALL_STIPULATION_CATEGORIES && APPLICANT_SCOPED_CATEGORIES.has(category) && scrollScoped) {
      if (applicantScope !== "both") {
        // Stips with no `applicantType` aren't scoped to either applicant, so
        // they stay visible regardless of which single column is active.
        result = result.filter((stip) => stip.applicantType === undefined || stip.applicantType === applicantScope);
      }
      result = withDriverFirstOrder(result);
    }

    return result;
  }, [items, category, applicantScope, scrollScoped]);

  const bankAccountOwnerOptions = buildBankAccountOwnerOptions({
    items,
    accountTypeLabels: {
      checking: t("stipulations.bankVerification.accountTypeChecking"),
      savings: t("stipulations.bankVerification.accountTypeSavings"),
    },
    applicantLabels: {
      driver: t("signers.driver"),
      secondSigner: t("signers.secondSigner"),
    },
    applicantFullNames: {
      driver: fullNameFromResidence(dashboard?.driver?.residence),
      secondSigner: fullNameFromResidence(dashboard?.secondSigner?.residence),
    },
  });

  return (
    <section className="border-border bg-card flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border">
      <header className="border-border bg-section-header flex items-center justify-between border-b px-4 py-1.5">
        <h2 className="text-foreground text-sm font-semibold">{t("stipulations.heading")}</h2>
        {openCount > 0 && (
          <StatusBadge
            status="open"
            label={t("stipulations.openCount", { n: openCount })}
            className="border-success-strong text-success-strong"
          />
        )}
      </header>
      <div className="flex items-center justify-between gap-2 px-4 py-2">
        <Select
          value={category}
          onValueChange={(value) => setCategory(value as StipulationCategory | typeof ALL_STIPULATION_CATEGORIES)}
        >
          <SelectTrigger size="sm" aria-label={t("stipulations.filterCategory")} className="bg-card h-8 w-44 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STIPULATION_CATEGORIES.map((code) => (
              <SelectItem key={code} value={code} className="text-xs">
                {code}
              </SelectItem>
            ))}
            <SelectItem value={ALL_STIPULATION_CATEGORIES} className="text-xs">
              {t("stipulations.filterAll")}
            </SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2 text-xs">
          <label htmlFor="stips-open-only" className="text-muted-foreground">
            {t("stipulations.openOnly")}
          </label>
          <Switch
            id="stips-open-only"
            checked={openOnly}
            onCheckedChange={setOpenOnly}
            className="data-[state=checked]:bg-success-strong"
          />
        </div>
      </div>
      {isLoading && (
        <div role="status" aria-label={t("stipulations.loading")} className="p-3">
          <Skeleton loading active paragraph={{ rows: 4 }} />
        </div>
      )}
      {!isLoading && isError && (
        <ErrorState message={t("stipulations.loadError", { message: getErrorMessage(error) })} />
      )}
      {!isLoading && !isError && visible.length === 0 && (
        <EmptyState
          message={
            category === ALL_STIPULATION_CATEGORIES
              ? t("stipulations.empty")
              : t("stipulations.emptyCategory", { category })
          }
        />
      )}
      {!isLoading && !isError && visible.length > 0 && (
        <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto p-0.5 xl:p-3">
          {visible.map((stip) => (
            <StipulationRow
              key={stip.id}
              approvalId={approvalId}
              stip={stip}
              bankAccountOwnerOptions={bankAccountOwnerOptions}
            />
          ))}
        </div>
      )}
    </section>
  );
}

export default StipulationsPanel;
